import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listOrganizationMembers,
  addOrganizationMember,
  inviteDiscoveredUser,
  removeOrganizationMember,
  resendOrganizationInvite,
  updateOrganizationMemberRole,
  listMembersOnPersonalKeys,
} from '@kirin/api-client/api/members';
import { OrganizationRole } from '@kirin/api-client/contracts/organizations';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from './queryKeys';
import { cacheConfigForCollaborativeData } from './queryConfig';

/**
 * Hook to fetch organization members list
 *
 * Uses React Query for caching and automatic refetching.
 * Uses collaborative cache config: refetches on window focus to catch changes from other admins.
 *
 * @param organizationId - The organization ID
 * @param enabled - Whether to enable the query (default: true if organizationId provided)
 *
 * @example
 * const { data: members, isLoading, error } = useMembers(organizationId);
 *
 * @returns React Query result with members array
 */
export function useMembers(organizationId: string | undefined, enabled = true) {
  const isEnabled = enabled && !!organizationId;
  return useQuery({
    queryKey: queryKeys.organizations.members(organizationId ?? ''),
    queryFn: () => {
      if (!organizationId) throw new Error('organizationId is required');
      return listOrganizationMembers(apiClient, organizationId);
    },
    enabled: isEnabled,
    ...cacheConfigForCollaborativeData,
  });
}

/**
 * Hook to fetch org members still using a personal API key (KIR-3786, AC2/AC3).
 *
 * Owner-only endpoint — gate `enabled` on the caller's owner role so
 * non-owners never fire a request that would 403.
 *
 * @param organizationId - The organization ID
 * @param enabled - Whether to run the query (default true when orgId provided)
 * @returns React Query result with the personal-key members array
 */
export function usePersonalKeyMembers(
  organizationId: string | undefined,
  enabled = true
) {
  const isEnabled = enabled && !!organizationId;
  return useQuery({
    queryKey: queryKeys.organizations.personalKeyMembers(organizationId ?? ''),
    queryFn: () => {
      if (!organizationId) throw new Error('organizationId is required');
      return listMembersOnPersonalKeys(apiClient, organizationId);
    },
    enabled: isEnabled,
    ...cacheConfigForCollaborativeData,
  });
}

type AddMemberParams = {
  organizationId: string;
  email: string;
  role?: OrganizationRole;
};

/**
 * Hook to add a member to an organization
 *
 * Uses React Query mutation with automatic cache invalidation.
 *
 * @example
 * const { mutateAsync: addMember, isPending, error } = useAddMember();
 * await addMember({ organizationId, email, role: 'member' });
 *
 * @returns React Query mutation result
 */
export function useAddMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      organizationId,
      email,
      role = OrganizationRole.Member,
    }: AddMemberParams) =>
      addOrganizationMember(apiClient, organizationId, {
        email,
        role,
      }),
    onSuccess: (_, { organizationId }) => {
      // Invalidate members list cache after successful addition
      // Note: We don't invalidate organizations list because adding a member
      // doesn't change which organizations the current user belongs to
      queryClient.invalidateQueries({
        queryKey: queryKeys.organizations.members(organizationId),
      });
    },
  });
}

/**
 * Hook to invite a Discovered user (KIR-3908), converting the lightweight row
 * into a full account while preserving its findings/alerts.
 *
 * @example
 * const { mutateAsync: invite, isPending } = useInviteDiscoveredUser();
 * await invite({ organizationId, userId });
 */
export function useInviteDiscoveredUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      organizationId,
      userId,
    }: {
      organizationId: string;
      userId: string;
    }) => inviteDiscoveredUser(apiClient, organizationId, userId),
    onSuccess: (_, { organizationId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.organizations.members(organizationId),
      });
    },
  });
}

/**
 * Hook to remove a member from an organization
 *
 * Uses React Query mutation with automatic cache invalidation.
 *
 * @example
 * const { mutateAsync: removeMember, isPending, error } = useRemoveMember();
 * await removeMember({ organizationId, userId });
 *
 * @returns React Query mutation result
 */
export function useRemoveMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      organizationId,
      userId,
    }: {
      organizationId: string;
      userId: string;
    }) => removeOrganizationMember(apiClient, organizationId, userId),
    onSuccess: (_, { organizationId }) => {
      // Invalidate members list cache after successful removal
      queryClient.invalidateQueries({
        queryKey: queryKeys.organizations.members(organizationId),
      });
    },
  });
}

/**
 * Hook to re-send a pending or expired invitation (KIR-4040).
 *
 * Mirrors {@link useRemoveMember}: rotates the invite token (invalidating the
 * old link) and re-sends the invitation email, then invalidates the members
 * list so the row refreshes (it stays "Invited"). Owner/manager only — the
 * route enforces the role independently.
 *
 * @example
 * const { mutateAsync: resendInvite, isPending, error } = useResendInvite();
 * await resendInvite({ organizationId, userId });
 *
 * @returns React Query mutation result
 */
export function useResendInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      organizationId,
      userId,
    }: {
      organizationId: string;
      userId: string;
    }) => resendOrganizationInvite(apiClient, organizationId, userId),
    onSuccess: (_, { organizationId }) => {
      // Invalidate members list cache so the resent row refreshes
      queryClient.invalidateQueries({
        queryKey: queryKeys.organizations.members(organizationId),
      });
    },
  });
}

/**
 * Hook to update a member's role in an organization
 *
 * Uses React Query mutation with automatic cache invalidation.
 *
 * @example
 * const { mutateAsync: updateRole, isPending, error } = useUpdateMemberRole();
 * await updateRole({ organizationId, userId, role: 'manager' });
 *
 * @returns React Query mutation result
 */
export function useUpdateMemberRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      organizationId,
      userId,
      role,
    }: {
      organizationId: string;
      userId: string;
      role: string;
    }) => updateOrganizationMemberRole(apiClient, organizationId, userId, role),
    onSuccess: (_, { organizationId }) => {
      // Invalidate members list cache after successful update
      queryClient.invalidateQueries({
        queryKey: queryKeys.organizations.members(organizationId),
      });
    },
  });
}
